import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCourses } from '@/hooks/useCourses'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { useForceLightMode } from '@/contexts/ThemeContext'
import type { Course } from '@/types/course'

const getLevelLabel = (courseType: string): string => {
    switch (courseType) {
        case 'pure_online': return 'Online'
        case 'hybrid': return 'Hybrid'
        case 'offline': return 'Offline'
        default: return 'Online'
    }
}

const CourseCard: React.FC<{ course: Course }> = ({ course }) => {
    return (
        <Link
            to={`/course/${course.id}`}
            className="group bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-white/5 overflow-hidden hover:shadow-2xl hover:shadow-slate-200/50 dark:hover:shadow-none transition-all duration-500 flex flex-col h-full"
        >
            <div className="relative aspect-[16/10] overflow-hidden">
                {course.thumbnailUrl ? (
                    <ImageWithFallback
                        src={course.thumbnailUrl}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                ) : (
                    <div className="w-full h-full bg-slate-100 dark:bg-white/5 flex items-center justify-center">
                        <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700">school</span>
                    </div>
                )}
                <div className="absolute top-4 right-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-3 py-1.5 rounded-xl text-xs font-bold text-slate-900 dark:text-white shadow-sm">
                    {getLevelLabel(course.courseType)}
                </div>
            </div>
            <div className="p-5 sm:p-8 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-4">
                    <span className="bg-primary/10 text-primary text-xs px-2.5 py-1 rounded-lg font-bold">
                        Kursus
                    </span>
                    <div className="flex items-center text-amber-500 gap-1">
                        <span className="material-symbols-outlined text-sm filled">star</span>
                        <span className="text-slate-500 dark:text-slate-400 text-xs font-bold">4.8</span>
                    </div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 line-clamp-2 group-hover:text-primary transition-colors leading-tight">
                    {course.title}
                </h3>
                {course.description && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 line-clamp-2 font-medium leading-relaxed flex-1">
                        {course.description}
                    </p>
                )}
                <div className="flex items-center justify-between pt-6 border-t border-slate-50 dark:border-white/5 mt-auto">
                    <div className="flex items-center text-slate-400 text-xs font-bold gap-1.5">
                        <span className="material-symbols-outlined text-[18px]">schedule</span>
                        {course.isPublished ? 'Tersedia Sekarang' : 'Segera Hadir'}
                    </div>
                    {course.courseType === 'hybrid' ? (
                        <span className="text-xl font-bold text-primary">Lihat Jadwal</span>
                    ) : (
                        <span className="text-xl font-bold text-slate-900 dark:text-white">
                            {course.price > 0 ? `Rp ${course.price.toLocaleString('id-ID')}` : 'Gratis'}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    )
}

export const DiscoverPage: React.FC = () => {
    useForceLightMode()
    const [searchQuery, setSearchQuery] = useState('')
    const [courseType, setCourseType] = useState<string>('')
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const { data: courses, isLoading, error } = useCourses({ isPublished: true }, true)

    const filteredCourses = courses?.filter(course => {
        const matchesSearch = !searchQuery ||
            course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            course.description?.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesType = !courseType || course.courseType === courseType
        return matchesSearch && matchesType
    }) || []

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-32 pb-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-16 space-y-4">
                    <h1 className="text-4xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">
                        Katalog <span className="relative inline-block">
                            <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-500 to-indigo-600">Kursus</span>
                            <span className="absolute bottom-2 left-0 w-full h-3 bg-primary/10 -rotate-1 rounded-full -z-0" />
                        </span>
                    </h1>
                    <p className="text-lg text-slate-500 dark:text-slate-400 font-medium max-w-2xl leading-relaxed">
                        Temukan program pelatihan eksklusif yang dirancang untuk mengakselerasi karir dan potensi Anda.
                    </p>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-6 mb-16 items-center">
                    <div className="relative flex-1 w-full group">
                        <span className="material-symbols-outlined absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors text-[22px]">search</span>
                        <input
                            type="text"
                            placeholder="Cari kursus idaman Anda..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-14 pr-6 py-4 rounded-[1.5rem] bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-primary/50 transition-all shadow-sm"
                        />
                    </div>

                    {/* Custom Dropdown */}
                    <div className="relative w-full sm:w-64">
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full px-6 py-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 rounded-[1.5rem] flex items-center justify-between text-sm font-bold text-slate-700 dark:text-slate-300 hover:border-primary/30 transition-all shadow-sm group"
                        >
                            <span>{courseType ? (courseType === 'pure_online' ? 'Online' : courseType === 'hybrid' ? 'Hybrid' : 'Offline') : 'Semua Tipe'}</span>
                            <span className={`material-symbols-outlined text-[20px] text-slate-400 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}>expand_more</span>
                        </button>

                        {isDropdownOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
                                <div className="absolute top-full left-0 right-0 mt-3 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-white/5 shadow-2xl z-20 py-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                                    {[
                                        { value: '', label: 'Semua Tipe' },
                                        { value: 'pure_online', label: 'Online' },
                                        { value: 'hybrid', label: 'Hybrid' },
                                        { value: 'offline', label: 'Offline' }
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                setCourseType(option.value)
                                                setIsDropdownOpen(false)
                                            }}
                                            className={`w-full px-6 py-3 text-left text-sm font-medium transition-colors ${courseType === option.value
                                                    ? 'bg-primary/5 text-primary'
                                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white'
                                                }`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-white/5 overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.04)] animate-pulse">
                                <div className="aspect-[16/10] bg-slate-100 dark:bg-white/5 rounded-[1.5rem] m-3"></div>
                                <div className="p-8 space-y-4 pt-2">
                                    <div className="h-3 bg-slate-100 dark:bg-white/5 rounded w-1/4"></div>
                                    <div className="h-10 bg-slate-100 dark:bg-white/5 rounded w-3/4"></div>
                                    <div className="h-4 bg-slate-100 dark:bg-white/5 rounded w-1/2"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-white/5">
                        <span className="material-symbols-outlined text-6xl text-rose-500 mb-4">error</span>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Gagal Memuat Kursus</h3>
                        <p className="text-slate-500 dark:text-slate-400 font-medium">Silakan refresh halaman atau coba lagi nanti.</p>
                    </div>
                )}

                {/* Empty State */}
                {!isLoading && !error && filteredCourses.length === 0 && (
                    <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-white/5">
                        <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 mb-4">school</span>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Tidak Ada Kursus Ditemukan</h3>
                        <p className="text-slate-500 dark:text-slate-400 font-medium">
                            {searchQuery ? 'Coba ubah kata kunci pencarian Anda' : 'Belum ada kursus yang tersedia saat ini'}
                        </p>
                    </div>
                )}

                {/* Course Grid */}
                {!isLoading && !error && filteredCourses.length > 0 && (
                    <>
                        <div className="flex items-center justify-between mb-10 pb-6 border-b border-slate-200 dark:border-white/10">
                            <p className="text-base font-bold text-slate-900 dark:text-white">
                                Menampilkan <span className="text-primary">{filteredCourses.length}</span> Kursus Tersedia
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                            {filteredCourses.map((course) => (
                                <div key={course.id} className="group bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden border border-slate-100 dark:border-white/5 shadow-[0_10px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-all duration-500 hover:-translate-y-2">
                                    <CourseCard course={course} />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default DiscoverPage
